// Core
import { Slot } from '@radix-ui/react-slot'
import { Loader2 } from 'lucide-react'
import React from 'react'
// Libs
import { tv, type VariantProps } from 'tailwind-variants'

const buttonVariants = tv({
  base: 'group',
  variants: {
    variant: {
      bare: '',
      'outline-secondary':
        'border border-[#cccccc] bg-transparent px-4 py-1 flex items-center justify-center rounded-[6px] gap-2 text-secondary text-sm font-semibold transition-colors enabled:hover:bg-zinc-100 disabled:text-text-disabled',
      primary:
        'bg-primary px-4 py-3 flex items-center justify-center rounded-[6px] gap-2 text-white font-semibold text-base transition-colors enabled:hover:bg-primaryDark disabled:bg-text-disabled',
      secondary:
        'bg-transparent px-4 py-3 border border-[#cccccc] flex items-center justify-center rounded-[6px] gap-2 text-primary font-semibold text-base transition-colors enabled:hover:bg-zinc-100 disabled:text-text-disabled',
      'ghost-primary':
        'bg-transparent px-4 py-1 flex items-center ju  stify-center rounded-[6px] gap-2 text-primary font-semibold text-base transition-colors enabled:hover:bg-zinc-100 disabled:text-text-disabled',
      'ghost-secondary':
        'bg-transparent px-2 py-1 flex items-center justify-center rounded-[6px] gap-2 text-secondary font-semibold text-xs transition-colors enabled:hover:bg-zinc-100 disabled:text-text-disabled',
      warning:
        'bg-warning px-6 py-3 flex items-center justify-center rounded-[6px] gap-2 text-white font-semibold text-base transition-colors enabled:hover:bg-warningDark disabled:bg-text-disabled',
      alert:
        'bg-error px-6 py-3 flex items-center justify-center rounded-[6px] gap-2 text-white font-semibold text-base transition-colors enabled:hover:bg-errorDark disabled:bg-text-disabled',
    },
    size: {
      md: 'h-10',
      lg: 'h-12',
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'lg',
  },
})

type ButtonVariants = VariantProps<typeof buttonVariants>

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  ButtonVariants & {
    isLoading?: boolean
    asChild?: boolean
  }

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant, size, className, isLoading, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'

    return (
      <Comp
        ref={ref}
        className={buttonVariants({ variant, size, className })}
        disabled={props.disabled ?? isLoading}
        {...props}
      >
        {isLoading && (
          <Loader2 strokeWidth={3} className="h-5 w-5 animate-spin" />
        )}
        {props.children}
      </Comp>
    )
  },
)

Button.displayName = 'Button'
